import { useState, useEffect, useCallback } from 'react';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import type { UsageStats, ApiResponse } from '../types';

/**
 * Hook to fetch usage statistics
 */
export function useUsage() {
  const authenticatedFetch = useAuthenticatedFetch();
  const [usage, setUsage] = useState<UsageStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authenticatedFetch('/api/billing/usage');

      // 404 means no subscription/usage - this is a valid state, not an error
      if (response.status === 404) {
        setUsage(null);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const json: ApiResponse<UsageStats> = await response.json();

      if (json.success && json.data) {
        setUsage(json.data);
      } else {
        throw new Error(json.error || 'Unknown API error');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch usage';
      setError(message);
      console.error('Usage fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { usage, loading, error, refetch: fetchData };
}
